import { useEffect, useRef } from "react";
import type { Product } from "../lib/api";

type ProductGridProps = {
  products: Product[];
  onOpen: (slug: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  preserved?: boolean;
};

export function ProductGrid({ products, onOpen, onLoadMore, hasMore, isLoadingMore, preserved }: ProductGridProps) {
  const loadMoreRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (preserved) return undefined;
    if (!hasMore || !onLoadMore || !loadMoreRef.current) return undefined;
    if (!("IntersectionObserver" in window)) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isLoadingMore) onLoadMore();
      },
      { rootMargin: "600px 0px" }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore, preserved]);

  return (
    <section className={`product-grid ${preserved ? "preserved" : ""}`} aria-label="Товары" aria-hidden={preserved}>
      {products.map((product) => (
        <button className="product-tile" key={product.id} onClick={() => onOpen(product.slug)} tabIndex={preserved ? -1 : 0}>
          <img
            src={product.media[0]?.url ?? "/placeholders/product.svg"}
            alt={product.media[0]?.altText ?? product.title}
            loading="lazy"
            decoding="async"
            onError={(event) => {
              event.currentTarget.src = "/placeholders/product.svg";
            }}
          />
          {product.media.length > 1 ? <span className="tile-carousel-badge" aria-label="Несколько фото" /> : null}
        </button>
      ))}
      {hasMore ? (
        <button className="load-more-tile" ref={loadMoreRef} onClick={onLoadMore} disabled={isLoadingMore} tabIndex={preserved ? -1 : 0}>
          {isLoadingMore ? "Загружаем..." : "Еще"}
        </button>
      ) : null}
    </section>
  );
}
